import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight, UserRound } from "lucide-react";
import Logo from "./Logo";
import LangSwitcher from "./LangSwitcher";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { id: "home", to: "/" },
  { id: "about", to: "/about" },
  { id: "fleet", to: "/fleet" },
  { id: "services", to: "/services" },
  { id: "driver", to: "/driver" },
  { id: "contact", to: "/contact" }
];

export default function Navbar() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const accountTo = user ? "/account" : "/auth";
  const accountLabel = user ? t.auth.myAccount : t.auth.signIn;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 border-b ${
          scrolled ? "bg-[#070709]/85 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 lg:px-10 h-[72px] flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo-link" aria-label="MIRAL GROUP home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map(({ id, to }) => (
              <NavLink
                key={id}
                to={to}
                end={to === "/"}
                data-testid={`nav-link-${id}`}
                className={({ isActive }) =>
                  `font-mono2 text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 ${
                    isActive ? "text-gold-soft" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {t.nav[id]}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangSwitcher className="hidden sm:flex" />
            <Link
              to={accountTo}
              data-testid="nav-auth-link"
              aria-label={accountLabel}
              title={accountLabel}
              className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 hover:text-gold-soft hover:border-gold/50 transition-all duration-300"
            >
              <UserRound size={17} />
            </Link>
            <Link
              to="/driver"
              data-testid="nav-cta-driver"
              className="hidden lg:inline-flex btn-outline !py-2.5 !px-5 text-[11px] font-mono2 uppercase tracking-[0.18em]"
            >
              {t.nav.driverCta}
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label="Menu"
              onClick={() => setOpen(!open)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 lg:hidden bg-[#070709]/97 backdrop-blur-2xl pt-[88px] px-6 pb-10 flex flex-col overflow-y-auto max-h-[100svh]"
          >
            <nav className="flex flex-col gap-1">
              {LINKS.map(({ id, to }, i) => (
                <motion.div
                  key={id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={to}
                    end={to === "/"}
                    data-testid={`mobile-nav-link-${id}`}
                    className={({ isActive }) =>
                      `flex items-center justify-between py-4 border-b border-white/8 font-display text-2xl uppercase tracking-tight ${
                        isActive ? "text-gold-soft" : "text-white"
                      }`
                    }
                  >
                    {t.nav[id]}
                    <ArrowRight size={18} className={`text-gold transition-transform ${lang === "ar" ? "rotate-180" : ""}`} />
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.32, duration: 0.4 }}>
                <NavLink
                  to={accountTo}
                  data-testid="mobile-nav-link-account"
                  className="flex items-center justify-between py-4 border-b border-white/8 font-display text-2xl uppercase tracking-tight text-white"
                >
                  {accountLabel}
                  <UserRound size={18} className="text-gold" />
                </NavLink>
              </motion.div>
            </nav>
            <div className="mt-auto flex flex-col gap-5">
              <LangSwitcher className="self-start" />
              <div className="flex flex-col gap-3">
                <Link to="/contact" data-testid="mobile-cta-contact" className="btn-gold w-full text-xs font-mono2 uppercase">
                  {t.nav.contactCta}
                </Link>
                <Link to="/driver" data-testid="mobile-cta-driver" className="btn-outline w-full text-xs font-mono2 uppercase">
                  {t.nav.driverCta}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
